import $ from 'jquery';
import moment from 'moment';
import type { Ref } from 'vue';
import { nextTick, reactive, ref, unref } from 'vue';

import { handler2, revalidateElement, timeformat, TIPOLOGIA_CLIENTE_NAMES, toFixed10 } from '#public/commons-global';
import type { FeOrders_FeOrders } from '#public_ts/commons/dbs-shared';
import type { ConfirmationFunction } from '#public_ts/components';

import { ERR, ORDER_START_TIME, PRICE_TABLE, type PriceModel, type PriceTier } from './globals';
import type { ProductFormData, ProductFormState } from './types';

export type SumParser = {
    quantity: number;
    QTY_MIN: number;
    PRICE_TABLE: PriceTier[];
    PRICE_FIXED: number;
    MODE_FN: ((quantity: number) => void) | null;
    price_model: PriceModel | null;
    pricePerItem: number;
    discount: number;
    orderExData: Record<string, unknown> | null;
    init(): void;
    updateQuantity(): void;
    calculate_seq(quantity: number): void;
    calculate_prog(quantity: number): void;
    calculate_custom: {
        CONSERVAZIONE_VM(this: SumParser, version: unknown, quantity: number): void;
        CONSERVAZIONE_PEC(this: SumParser, version: string, quantity: number): void;
        CONSERVAZIONE_VIP(this: SumParser): void;
    };
    updatePromotion(options?: { nowait?: boolean; errors?: boolean }): Promise<void>;
    updateUserDiscountCode(): Promise<void>;
    updateSum(): void;
};

export function createSumParser(
    formData: ProductFormData,
    formState: ProductFormState,
    alert: ConfirmationFunction,
    onValidate?: (id: string) => void,
) {
    formState.isPromotionCodeValid = formState.isPromotionCodeValid ?? true;
    formState.isUserDiscountCodeAvailable = formState.isUserDiscountCodeAvailable ?? true;

    const sumParser = reactive<SumParser>({
        quantity: 0,
        QTY_MIN: 0,
        PRICE_TABLE: [],
        PRICE_FIXED: 0,
        MODE_FN: null,
        price_model: null,
        pricePerItem: 0,
        discount: 0,
        orderExData: null,

        init() {
            if(ERR) {
                return;
            }
            const product_id = formData.Product_Id;
            const price_model = PRICE_TABLE?.price_model;
            if(!price_model) {
                alert(`Cannot find suitable price model for product ${product_id}`);
                return;
            }
            this.QTY_MIN = price_model.qty_min || 0;
            this.PRICE_TABLE = price_model.prices;
            this.PRICE_FIXED = price_model.price_fixed || 0;
            const mode = price_model.mode;
            let fn: ((quantity: number) => void) | undefined;
            if(mode == 'custom') {
                const custom = this.calculate_custom as unknown as Record<string, ((...args: unknown[]) => void) | undefined>;
                const raw = price_model.handler ? custom[price_model.handler] : undefined;
                if(typeof raw !== 'function') {
                    alert(`Invalid price handler ${price_model.handler} for product ${product_id}`);
                    return;
                }
                const handlerparams = Array.isArray(price_model.handlerparams) ? price_model.handlerparams : [];
                fn = (quantity) => {
                    raw.call(this, ...handlerparams, quantity);
                };
            } else {
                const base = this as unknown as Record<string, ((quantity: number) => void) | undefined>;
                const raw = base[`calculate_${mode}`];
                if(typeof raw !== 'function') {
                    alert(`Invalid price mode ${mode} for product ${product_id}`);
                    return;
                }
                fn = (quantity) => {
                    raw.call(this, quantity);
                };
            }
            this.MODE_FN = fn;
            this.price_model = price_model;
        },

        updateQuantity() {
            if(ERR) {
                return;
            }
            let quantity = Number(formData.Product_Quantity);
            if(isNaN(quantity) || (quantity < 0)) {
                quantity = 0;
            }

            this.MODE_FN?.(quantity);
            this.quantity = quantity;
            if(this.orderExData) {
                formData.Order_Ex_Data = JSON.stringify(this.orderExData);
            }
            this.updateSum();
        },

        calculate_seq(quantity: number) {
            let pricePerItem = 0;
            for(let i = this.PRICE_TABLE.length - 1; i >= 0; i--) {
                const t = this.PRICE_TABLE[i];
                if(t && (quantity >= t.q)) {
                    pricePerItem = t.p;
                    break;
                }
            }

            this.pricePerItem = pricePerItem;
        },

        calculate_prog(quantity: number) {
            let pricePerItem = 0;
            let s = 0;
            let l = 0;
            let t: PriceTier | undefined;

            for(const tier of this.PRICE_TABLE) {
                t = tier;
                if(tier.q >= quantity) {
                    break;
                } else {
                    const d = tier.q - l;
                    s += tier.p * d;
                    l += d;
                }
            }
            s += (t!).p * (quantity - l);
            pricePerItem = s / quantity;

            if(quantity < this.QTY_MIN) {
                pricePerItem = 0;
            }

            this.pricePerItem = pricePerItem;
        },

        calculate_custom: {
            CONSERVAZIONE_VM(_version: unknown, quantity: number) {
                function roundToQuantity(imptot: number, quantity2: number) {
                    let pricePerItem = Number(imptot) / quantity2;
                    pricePerItem = Number((Math.round(pricePerItem * 100) / 100).toFixed(2));
                    return pricePerItem;
                }

                this.pricePerItem = 0;
                if(!quantity || (quantity < this.QTY_MIN)) {
                    formState.first_periodo = '';
                    formState.importo_totale = '';
                    formState.importo_totale_next = '';
                    return;
                }
                let imptot: number;
                const tD = ORDER_START_TIME ? new timeformat(ORDER_START_TIME).toMoment() : moment();
                let fD = tD.clone().add(1, 'year');
                //for JAN,FEB,MAR we give begin of year (thus ordering 10+ months), for others we give end of year (thus ordering 12+ months)
                if(tD.month() <= 2) {
                    fD = fD.startOf('year');
                } else {
                    fD = fD.endOf('year');
                }
                let mdiff = Math.ceil(Number(fD.diff(tD, 'month', true)));
                if(mdiff < 1) {
                    mdiff = 1;
                }

                const numdis = quantity;
                if(numdis < 7) {
                    imptot = 40 + (10 * numdis) + (2 * numdis);
                } else {
                    imptot = 100 + (3 * numdis);
                }

                formState.importo_totale_next = Number(roundToQuantity(imptot, quantity) * quantity).toFixed(2);
                const impper = (imptot / 12) * mdiff;
                const impperarrot = impper.toFixed(2);
                formState.first_periodo = `dal ${
                    new timeformat(tD).toDisplayTime({ needTime: false })
                } al ${
                    new timeformat(fD).toDisplayTime({ needTime: false })}`;
                const pricePerItem = roundToQuantity(Number(impperarrot), quantity);
                formState.importo_totale = Number(pricePerItem * quantity).toFixed(2);
                this.pricePerItem = pricePerItem;
            },

            CONSERVAZIONE_PEC(version: string, _quantity: number) {
                this.pricePerItem = 0;
                this.orderExData = null;
                formState.first_periodo = '';

                let yearsvalidity: number;
                let pricePerItem: number | null = null;
                if(version == 'v1') {
                    const selected = formData.yearsvalidity;
                    if(!selected || isNaN(Number(selected))) {
                        return;
                    }
                    yearsvalidity = Number(selected);

                    if(yearsvalidity == 1) {
                        pricePerItem = 19;
                    } else if(yearsvalidity == 10) {
                        pricePerItem = 39;
                    }
                } else if(version == 'v2') {
                    yearsvalidity = 1;
                    pricePerItem = 9.9;
                } else if(version == 'v3') {
                    yearsvalidity = 1;
                    pricePerItem = 19.9;
                } else if(version == 'v4') {
                    yearsvalidity = 1;
                    pricePerItem = 49.9;
                } else if(version == 'v5') {
                    yearsvalidity = 1;
                    pricePerItem = 69.9;
                } else {
                    return;
                }

                if(!pricePerItem) {
                    return;
                }

                const now = ORDER_START_TIME ? new timeformat(ORDER_START_TIME).toMoment() : moment();
                const periodo = now.clone().add(1, 'year').add(-1, 'day');
                const dal = new timeformat(now).toDisplayTime({ needTime: false });
                const al = new timeformat(periodo).toDisplayTime({ needTime: false });
                formState.first_periodo = `${dal} - ${al}`;

                this.pricePerItem = pricePerItem;
                this.orderExData = { yearsvalidity };
            },

            CONSERVAZIONE_VIP() {
                this.pricePerItem = Number(formState.Product_Prezzo);
                if(!this.pricePerItem) {
                    this.pricePerItem = 0;
                }
                this.orderExData = { pricePerItem: toFixed10(this.pricePerItem, 2) };
            },
        },

        updatePromotion({ nowait = true, errors }: { nowait?: boolean; errors?: boolean } = {}) {
            let code: string | undefined;
            if(formState.Product_Promotion_Code_flag) {
                code = formData.Product_Promotion_Code;
            }
            if(!code) {
                this.discount = 0;
                formState.isPromotionCodeValid = true;
                this.updateSum();
                return Promise.resolve();
            }
            const query = {
                code,
                product: formData.Product_Id,
                Company_Tax_Number: formData.Company_Tax_Number || null,
                Company_Fiscal_Code: formData.Company_Fiscal_Code || null,
            };
            return handler2.handle('PromotionCodesHandler', query, { nowait, errors })
                .then((response) => {
                    const data = response.data;
                    if(data.available) {
                        formState.isPromotionCodeValid = true;
                    } else {
                        formState.isPromotionCodeValid = data.errorMessage || false;
                    }
                    onValidate?.('Product_Promotion_Code');
                    this.discount = Number(data.discount || 0);
                    this.updateSum();
                });
        },

        async updateUserDiscountCode() {
            const code = formData.User_Discount_Code;
            if(!code) {
                formState.isUserDiscountCodeAvailable = true;
                return;
            }
            const query = {
                code,
                mode: 'agent',
            };
            const response = await handler2.handle('PromotionCodesHandler', query, { nowait: true });
            const data = response.data;
            formState.isUserDiscountCodeAvailable = data.available;
            onValidate?.('User_Discount_Code');
        },

        updateSum() {
            if(this.pricePerItem == 0) {
                formState.Product_Prezzo = '';
                formState.Product_Percentuale_Sconto = '';
                formState.Product_Subtotale = '';
                formState.Product_IVA = '';
                formState.Prezzo_Totale = '';
                formState.Totale_Iva = '';
                return;
            }
            formState.Product_Prezzo = this.pricePerItem.toFixed(2);
            formState.Product_Percentuale_Sconto = String(this.discount);
            const discountOneEur = Math.round(this.pricePerItem * this.discount) / 100;
            const discountOnePrice = Math.round((this.pricePerItem - discountOneEur) * 100) / 100;
            formState.Product_Singolo_Canone = discountOnePrice.toFixed(2);
            const totalPrice = this.pricePerItem * this.quantity + this.PRICE_FIXED;
            const discountEur = Math.round(totalPrice * this.discount) / 100;
            const discountPrice = Math.round((totalPrice - discountEur) * 100) / 100;
            formState.Product_Subtotale = discountPrice.toFixed(2);
            const TotaleIva = (discountOnePrice * this.quantity * 1.22);
            formState.Totale_Iva = TotaleIva.toFixed(2);
            const iva = Math.round(discountPrice * 22) / 100;
            formState.Product_IVA = iva.toFixed(2);
            formState.Prezzo_Totale = (Math.round((discountPrice + iva) * 100) / 100).toFixed(2);
        },
    });

    return sumParser;
}

type FieldMapping = string | {
    ctl?: string;
    fn?: (val: unknown) => void;
    boolean?: boolean;
};

type FieldMappings = Record<string, FieldMapping>;

export function createFormInitializer(
    formData: ProductFormData,
    _formState: ProductFormState,
) {
    const AD_SOURCES: Record<string, string> = {
        ad_type_1: 'Motori di ricerca',
        ad_type_2: 'RAI TV',
        ad_type_3: 'Radio 24',
        ad_type_4: 'Il Sole 24 Ore',
        ad_type_5: 'La Gazzetta dello Sport',
        ad_type_6: 'PMI',
        ad_type_7: 'Conoscenti',
        ad_type_8: 'Newsletter CompEd',
        ad_type_9: 'Segnalazione da parte di altri utenti',
        ad_type_10: 'Sono già cliente CompEd Servizi',
        ad_type_11: 'Sky TG24',
        ad_type_12: 'MEDIASET TGCOM24',
        ad_type_13: 'Facebook',
    };
    const RAD_SOURCES: Record<string, string> = {};
    for(const name in AD_SOURCES) {
        RAD_SOURCES[AD_SOURCES[name]!] = name;
    }

    function fillAdsFields(order: Record<string, unknown>) {
        const record = order;
        const adSource = record['ad_source'];
        if(typeof adSource !== 'string' || (adSource.length == 0)) {
            return;
        }
        const fields = adSource.split(',');
        const ad_type_0: string[] = [];
        for(const field of fields) {
            const id = RAD_SOURCES[field];
            if(id) {
                formData[id] = true;
            } else {
                ad_type_0.push(field);
            }
        }
        formData.ad_type_0 = ad_type_0.join(',');
    }

    function fillOrderFieldsFromJson(order: Record<string, unknown>, mappings: FieldMappings) {
        const record = order;
        for(const name in mappings) {
            const val = record[name];
            let map = mappings[name];
            if(typeof map === 'string') {
                map = { ctl: map };
            }
            if(!map) {
                continue;
            }
            if(map.fn) {
                map.fn(val);
            } else if(map.ctl) {
                formData[map.ctl] = map.boolean ? (val == '1') : val;
            }
        }
    }

    function fillOrderFields(order: FeOrders_FeOrders) {
        const mappings: FieldMappings = {
            codice_destinatario: 'codice_destinatario',
            user_name: 'Name',
            user_surname: 'Surname',
            user_title: 'Title',
            user_position: 'Position',
            user_email: 'Personal_Email',
            pec_email: 'PEC_Email',
            user_tel: 'Personal_tel',
            user_mobile: 'Personal_cel',
            company_name: 'Company_Name',
            company_address: 'Company_address',
            company_postindex: 'Company_PostIndex',
            company_city: 'Company_City',
            company_province: 'Company_Province',
            company_vat_number: 'Company_Tax_Number',
            company_fiscal_code: 'Company_Fiscal_Code',
            send_email_flag: { ctl: 'Service_FE_send_email_flag', boolean: true },
            send_email: 'Service_FE_send_email',
            send_email_2: 'Service_FE_send_email_2',
            format_selection_txt: { ctl: 'format_selection_txt', boolean: true },
            format_selection_word: { ctl: 'format_selection_word', boolean: true },
            format_selection_exel: { ctl: 'format_selection_exel', boolean: true },
            format_selection_pdf: { ctl: 'format_selection_pdf', boolean: true },
            format_selection_img: { ctl: 'format_selection_img', boolean: true },
            format_selection_other: 'format_selection_other',
            payment_method: {
                fn: (val) => {
                    if(!val) {
                        return;
                    }
                    if((val as string).toLowerCase() == 'bank') {
                        formData.payment_method = 'bank';
                    } else {
                        formData.payment_method = 'gestpay';
                    }
                },
            },
            company_type: {
                fn: (val) => {
                    if(!val) {
                        return;
                    }
                    const value = (val as string).toLowerCase();
                    for(const [name, tipologia] of Object.entries(TIPOLOGIA_CLIENTE_NAMES)) {
                        if(tipologia && (value.includes(tipologia.name))) {
                            formData[name] = true;
                            break;
                        }
                    }
                },
            },
        };
        fillOrderFieldsFromJson(order, mappings);
        fillAdsFields(order);
    }

    function fillOrderFieldsFormFattappQr(data: Record<string, unknown>) {
        const mappings: FieldMappings = {
            Nome: 'Name',
            Cognome: 'Surname',
            Titolo: 'Title',
            email: 'Personal_Email',
            cellulare: 'Personal_cel',
            Denominazione: 'Company_Name',
            Indirizzo: 'Company_address',
            CAP: 'Company_PostIndex',
            Comune: 'Company_City',
            Provincia: 'Company_Province',
            IdCodice: 'Company_Tax_Number',
            CodiceFiscale: 'Company_Fiscal_Code',
        };
        fillOrderFieldsFromJson(data, mappings);
    }

    return {
        fillOrderFields,
        fillOrderFieldsFormFattappQr,
    };
}


export function createFormCheckboxState(formData: ProductFormData) {
    const COMPANY_TYPES = [
        'company_type_0',
        'company_type_1',
        'company_type_2',
        'company_type_3',
        'company_type_4',
        'company_type_5',
    ] as const;
    type CompanyTypeCheckbox = typeof COMPANY_TYPES[number];

    type MarketingCheckbox = 'privacy_marketing_ours'
        | 'privacy_marketing_group'
        | 'privacy_marketing_profilazione'
        | 'privacy_data_transfer_ex_ue';

    function revalidate() {
        const company_type = $(
            '#company_type_0,#company_type_1,#company_type_2,#company_type_3,#company_type_4,#company_type_5,.checkbox-category-cliente',
        );
        company_type.each(function() {
            revalidateElement(this);
        });
    }
    function companyTypeChanged(id: CompanyTypeCheckbox, state: boolean) {
        // if we just set true vue won't notice changes
        state = !!state;
        if(formData[id] === state) return;
        formData[id] = state;
        nextTick(() => {
            setTimeout(() => {
                // no uncheck
                for(const _id of COMPANY_TYPES) {
                    if(_id === id) {
                        formData[_id] = true;
                    } else {
                        formData[_id] = false;
                    }
                }
                formData.category_cliente = '';
                revalidate();
            }, 10);
        });
    }

    function categoryClienteChanged(id: string, state: boolean) {
        if(!id) return;
        // if we just set true vue won't notice changes
        const val = state ? id : '';
        if(formData.category_cliente === val) return;
        formData.category_cliente = val;
        nextTick(() => {
            setTimeout(() => {
                // no uncheck
                for(const _id of COMPANY_TYPES) {
                    formData[_id] = false;
                }
                formData.category_cliente = id;
                revalidate();
            }, 10);
        });
    }

    function privacyMarketingChanged(id: MarketingCheckbox, state: boolean) {
        const _on = `${id}_on` as const;
        const _off = `${id}_off` as const;
        if(state) {
            formData[_on] = true;
            formData[_off] = false;
        } else {
            formData[_on] = false;
            formData[_off] = true;
        }
    }

    return {
        companyTypeChanged,
        categoryClienteChanged,
        privacyMarketingChanged,
    };
}

function createIframeButtonState(iframeDiv: Ref<HTMLElement | undefined>) {
    const buttonText = ref('Ingrandisci');
    const buttonMaximized = ref(false);
    function toggle() {
        const _iframeDiv = unref(iframeDiv);
        if(!_iframeDiv) return;
        const text = $(_iframeDiv);
        const iframe = text.children('iframe');
        buttonMaximized.value = !buttonMaximized.value;
        if(buttonMaximized.value) {
            buttonText.value = ('Riduci');
            iframe.animate({ height: '+=400px' }, 'fast');
            text.css('height', '500');
        } else {
            buttonText.value = ('Ingrandisci');
            iframe.animate({ height: '-=400px' }, 'fast');
            text.css('height', '');
        }
    }

    return {
        buttonText,
        buttonMaximized,
        toggle,
    };
}

export function createIframeAllButtonState() {
    const Regolamento_text = ref<HTMLElement | undefined>();
    const Regolamento_Assistenza_text = ref<HTMLElement | undefined>();
    const ConvenzioneDiServizio_text = ref<HTMLElement | undefined>();
    const Privacy_text = ref<HTMLElement | undefined>();

    const Regolamento = createIframeButtonState(Regolamento_text);
    const Regolamento_Assistenza = createIframeButtonState(Regolamento_Assistenza_text);
    const ConvenzioneDiServizio = createIframeButtonState(ConvenzioneDiServizio_text);
    const Privacy = createIframeButtonState(Privacy_text);

    return {
        Regolamento_text,
        Regolamento_button: Regolamento.buttonText,
        Regolamento_maximized: Regolamento.buttonMaximized,
        Regolamento_toggle: Regolamento.toggle,
        Regolamento_Assistenza_text,
        Regolamento_Assistenza_button: Regolamento_Assistenza.buttonText,
        Regolamento_Assistenza_maximized: Regolamento_Assistenza.buttonMaximized,
        Regolamento_Assistenza_toggle: Regolamento_Assistenza.toggle,
        ConvenzioneDiServizio_text,
        ConvenzioneDiServizio_button: ConvenzioneDiServizio.buttonText,
        ConvenzioneDiServizio_maximized: ConvenzioneDiServizio.buttonMaximized,
        ConvenzioneDiServizio_toggle: ConvenzioneDiServizio.toggle,
        Privacy_text,
        Privacy_button: Privacy.buttonText,
        Privacy_maximized: Privacy.buttonMaximized,
        Privacy_toggle: Privacy.toggle,
    };
}
